import * as React from 'react';
import {
  DocumentParseErrorCode,
  DocumentParseExtractedField,
  DocumentParseStatus,
  getDocumentParseStatus,
  TERMINAL_DOCUMENT_PARSE_STATUSES,
  triggerDocumentParse,
} from 'src/api/documentParse';

const POLL_INTERVAL_MS = 5000;
/**
 * Gives up on a parse that never reaches a terminal status (e.g. the agent callback is lost). Sized
 * to the agent's real turnaround, not to what feels snappy: a result that lands after this window is
 * still written to the job row, but nothing on screen is listening for it any more.
 */
const POLL_TIMEOUT_MS = 5 * 60 * 1000;

export type DocumentParseState = {
  isParsing: boolean;
  extractedFields: DocumentParseExtractedField[];
  errorMessage: string;
  attemptsUsed?: number;
  attemptsAllowed?: number;
  /** True when the attempt window is exhausted — a wait, not a failure. */
  isAttemptLimitReached: boolean;
  /** Epoch ms at which the next attempt unlocks. Only set alongside isAttemptLimitReached. */
  attemptsResetAt?: number;
};

const INITIAL_STATE: DocumentParseState = {
  isParsing: false,
  extractedFields: [],
  errorMessage: '',
  isAttemptLimitReached: false,
};

/**
 * Document parse & field prefill (ISS-292181).
 *
 * One parse per uploaded document: `startParse` fires `POST /document-parse/trigger` and then
 * short-polls `GET /document-parse/status/:parseJobId` until the parse reaches a terminal state.
 * The returned `extractedFields` are SUGGESTIONS — the caller fills only inputs that are still
 * empty, and nothing persists until the user submits the task.
 *
 * The agent reports no progress, so there is nothing to render a determinate bar from — the caller
 * shows an indeterminate spinner for as long as `isParsing` holds.
 *
 * Parsing being unavailable for a task/document type is a normal outcome, not an error: the trigger
 * answers 400 EDOCPARSE001 and the hook simply stays idle so the user types the values by hand.
 */
const useDocumentParse = () => {
  const [state, setState] = React.useState<DocumentParseState>(INITIAL_STATE);

  const pollTimer = React.useRef<number | undefined>(undefined);
  const deadline = React.useRef<number>(0);
  /** Guards every async continuation: the user can close the task drawer mid-parse. */
  const isMounted = React.useRef<boolean>(true);

  const clearTimers = React.useCallback(() => {
    if (pollTimer.current) {
      window.clearTimeout(pollTimer.current);
      pollTimer.current = undefined;
    }
  }, []);

  React.useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
      clearTimers();
    };
  }, [clearTimers]);

  const finish = React.useCallback(
    (patch: Partial<DocumentParseState>) => {
      clearTimers();
      if (!isMounted.current) return;
      setState((prev) => ({ ...prev, ...patch, isParsing: false }));
    },
    [clearTimers]
  );

  const poll = React.useCallback(
    async (parseJobId: string) => {
      if (!isMounted.current) return;

      if (Date.now() > deadline.current) {
        finish({
          errorMessage: 'Could not read this document in time. Please enter the values manually.',
        });
        return;
      }

      const response = await getDocumentParseStatus(parseJobId);
      if (!isMounted.current) return;

      if (!response.isSuccess || !response.data) {
        finish({ errorMessage: response.errorMessage || 'Could not fetch document parsing status.' });
        return;
      }

      const {
        parseStatus,
        extractedFields,
        error,
        attemptsUsed,
        attemptsAllowed,
        attemptsResetAt,
      } = response.data;

      if (!TERMINAL_DOCUMENT_PARSE_STATUSES.includes(parseStatus)) {
        pollTimer.current = window.setTimeout(() => poll(parseJobId), POLL_INTERVAL_MS);
        return;
      }

      if (parseStatus === DocumentParseStatus.PARSED) {
        finish({
          extractedFields: extractedFields || [],
          errorMessage: '',
          attemptsUsed,
          attemptsAllowed,
          attemptsResetAt,
        });
        return;
      }

      finish({
        extractedFields: [],
        errorMessage: error || 'Could not read this document. Please enter the values manually.',
        attemptsUsed,
        attemptsAllowed,
        attemptsResetAt,
      });
    },
    [finish]
  );

  const startParse = React.useCallback(
    async (params: {
      shipmentId: string;
      taskId: string;
      docUrl: string;
      docType: string;
      documentTemplateId: string;
      /** The uploaded file's own `File.type`; the agent gets it as `doc_mime_type`. */
      docMimeType?: string;
    }) => {
      clearTimers();
      setState({ ...INITIAL_STATE, isParsing: true });

      const response = await triggerDocumentParse(params);
      if (!isMounted.current) return;

      // Checked BEFORE the failure guard below: the attempt cap answers 200 with no parseJobId, which
      // that guard would otherwise read as a failure and render as a blank error line.
      if (response.isSuccess && response.data?.isAttemptLimitReached) {
        clearTimers();
        const { attemptsUsed, attemptsAllowed, attemptsResetAt } = response.data;
        // `message` is deliberately not stored: the caller renders its own live countdown off
        // `attemptsResetAt`, and keeping the server's snapshot of the same sentence would either sit
        // unread or contradict the countdown a minute later.
        setState({ ...INITIAL_STATE, isAttemptLimitReached: true, attemptsUsed, attemptsAllowed, attemptsResetAt });
        return;
      }

      if (!response.isSuccess || !response.data?.parseJobId) {
        clearTimers();
        // EDOCPARSE001 alone stays quiet: the trigger fires for every uploaded document, so on a task
        // where nothing is configured it would put a red line under every single upload. Every other
        // failure — a permissions or validation rejection, the network — is shown, using the backend's
        // own message rather than a guess at what went wrong.
        const isUnavailable = response.errorCode === DocumentParseErrorCode.NOT_AVAILABLE;
        setState({
          ...INITIAL_STATE,
          errorMessage: isUnavailable ? '' : response.errorMessage || '',
        });
        return;
      }

      const { parseJobId, attemptsUsed, attemptsAllowed } = response.data;
      setState((prev) => ({ ...prev, attemptsUsed, attemptsAllowed }));

      deadline.current = Date.now() + POLL_TIMEOUT_MS;
      poll(parseJobId);
    },
    [clearTimers, poll]
  );

  const reset = React.useCallback(() => {
    clearTimers();
    setState(INITIAL_STATE);
  }, [clearTimers]);

  return { ...state, startParse, reset };
};

export default useDocumentParse;
